import React from "react";
import { UserPermission } from "../../types/userPermission";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import { userHasPermissionAnyLine } from "../../aplUtils/permissions";
import { useAppDispatch } from "../../redux/hooks";
import { ReduxStatePalletSearchSort } from "../../types/redux/reduxStatePalletSearchSort";
import { useSelector } from "react-redux";
import { IRootState } from "../../types/redux/IRootStore";
import { PrinterLineSelection } from "../../types/printerLineSelection";
import {
    getAplLinesRedux,
    getPrintersDepartmentAssignmentsRedux,
    getPrintersLineAssignmentsRedux,
    getPrintersRedux,
    onClearAdminRedux,
    savePrinterLineSelectionRedux,
    savePrintersDepartmentAssignmentsRedux,
    setDepartmentPrintersRedux
} from "../../redux/slices/admin.feature";
import { adminPrinterLineColumns, adminPrinterDepartmentColumns } from "./AdminPrintersColumns";
import { setPrinterLineAssignmentsRedux } from "../../redux/slices/admin.feature";
import { DtGridPropsOptions2 } from "../../types/dtGridPropsOptions2";
import { DtGrid2 } from "../common/DtGrid2";
import _ from "lodash";
import { PrinterType } from "../../types/printerType";
import { Department } from "../../types/department";

const AdminPrinterLine = () => {
    const aplSettings = AplSettingsSelector();
    const isGlobalAdmin = userHasPermissionAnyLine(aplSettings.permsSets, UserPermission.GlobalPermsAdmin);
    const dispatch = useAppDispatch();
    let adminRedux = useSelector((state: IRootState) => state.admin);

    const [linePrintersGridFilters, setLinePrintersGridFilters] = React.useState({});
    const [linePrintersGridSort, setLinePrintersGridSort] = React.useState<ReduxStatePalletSearchSort>({
        field: ["lineId"],
        direction: ["asc"]
    });
    const [linePrintersGridPage, setLinePrintersGridPage] = React.useState(1);

    const updatePrinterLine = (lineId: number, printerId: number, printerType: PrinterType) => {
        console.log("updating", lineId, printerId, printerType);
        let updatedPrinterLineSelection = updatePrinterLineSelection(adminRedux.printerLineSelection, printerId, lineId, printerType);
        dispatch(setPrinterLineAssignmentsRedux(updatedPrinterLineSelection));
    };

    const updatePrinterDepartment = (departmentId: number, printerId: number, printerType: PrinterType) => {
        console.log("updating", departmentId, printerId, printerType);
        let updatedPrinterDepartmentSelection = updatePrinterDepartmentSelection(adminRedux.departments, printerId, departmentId, printerType);
        dispatch(setDepartmentPrintersRedux(updatedPrinterDepartmentSelection));
    };

    const updatePrinterLineSelection = (data: PrinterLineSelection[], printerId: number, lineId: number, printerType: PrinterType) => {
        return data.map((lps: PrinterLineSelection) => {
            if (lps.lineId === lineId && printerType === PrinterType.LinePrinterSelectionLabelling1) {
                return { ...lps, labellingPrinterId: printerId };
            } else if (lps.lineId === lineId && printerType === PrinterType.LinePrinterSelectionLabelling2) {
                return { ...lps, secondLabellingPrinterId: printerId };
            } else if (lps.lineId === lineId && printerType === PrinterType.LinePrinterSelectionBackup) {
                return { ...lps, backupPrinterId: printerId };
            } else if (lps.lineId === lineId && printerType === PrinterType.LinePrinterSelectionReturn) {
                return { ...lps, returnPrinterId: printerId };
            } else if (lps.lineId === lineId && printerType === PrinterType.AutoLabellerPrinter) {
                return { ...lps, labellerPrinterId: printerId };
            } else {
                return lps;
            }
        });
    };

    const updatePrinterDepartmentSelection = (data: Department[], printerId: number, departmentId: number, printerType: PrinterType) => {
        return data.map((lps: Department) => {
            if (lps.departmentId === departmentId && printerType === PrinterType.DepartmentPrinter1) {
                return { ...lps, backupDeptPrinterId : printerId };
            } else if (lps.departmentId === departmentId && printerType === PrinterType.DepartmentPrinter2) {
                return { ...lps, backupDeptPrinterId2: printerId };
            } else {
                return lps;
            }
        });
    };

    const savePrinterLineSelection = () => {
        dispatch(savePrinterLineSelectionRedux(adminRedux.printerLineSelection) as any);
    };

    const savePrinterDepartment = () => {
        dispatch(savePrintersDepartmentAssignmentsRedux(adminRedux.departments) as any);
    };

    const linePrintersGridOptions: DtGridPropsOptions2 = {
        data: adminRedux.printerLineSelection.map(x => ({
            ...x,
            lineDescription: adminRedux.lines.find(y => y.lineId === x.lineId)?.lineDescription,
            lineSapCode: adminRedux.lines.find(y => y.lineId === x.lineId)?.lineSapCode,
            linePlantCode: adminRedux.lines.find(y => y.lineId === x.lineId)?.linePlantCode
        })),
        columnData: adminPrinterLineColumns(
            _.uniqBy(adminRedux.printers, x => x.printerId),
            updatePrinterLine
        ),
        defaultSort: {
            field: ["userName"],
            direction: ["asc"]
        },
        selectType: "none",
        colouring: true,
        headerFilters: false,
        setSelectedItems: () => {},
        selectedItems: [],
        tableName: "tblPrinterLine",
        tableClass: "tbl-printer-line height-40rem",
        paging: true,
        showSpinner: false,
        rowId: "linePrinterSelectionId",
        loading: false,
        pageLength: 20,
        sort: linePrintersGridSort,
        filters: linePrintersGridFilters,
        setFilters: (x: any) => {
            setLinePrintersGridFilters(x);
        },
        setSort: (x: any) => {
            setLinePrintersGridSort(x);
        },
        setPage: (x: number) => {
            setLinePrintersGridPage(x);
        },
        page: linePrintersGridPage
    };

    const departmentPrintersGridOptions: DtGridPropsOptions2 = {
        data: adminRedux.departments,
        columnData: adminPrinterDepartmentColumns(
            _.uniqBy(adminRedux.printers, x => x.printerId),
            updatePrinterDepartment
        ),
        defaultSort: {
            field: ["userName"],
            direction: ["asc"]
        },
        selectType: "none",
        colouring: true,
        headerFilters: false,
        setSelectedItems: () => {},
        selectedItems: [],
        tableName: "tblPrinterDepartment",
        tableClass: "tbl-printer-department height-40rem",
        paging: true,
        showSpinner: false,
        rowId: "departmentId",
        loading: false,
        pageLength: 20,
        sort: linePrintersGridSort,
        filters: linePrintersGridFilters,
        setFilters: (x: any) => {
            setLinePrintersGridFilters(x);
        },
        setSort: (x: any) => {
            setLinePrintersGridSort(x);
        },
        setPage: (x: number) => {
            setLinePrintersGridPage(x);
        },
        page: linePrintersGridPage
    };

    React.useEffect(() => {
        dispatch(getAplLinesRedux(aplSettings.siteId) as any);
        dispatch(getPrintersLineAssignmentsRedux(aplSettings.siteId) as any);
        dispatch(getPrintersDepartmentAssignmentsRedux(aplSettings.siteId) as any);
        dispatch(getPrintersRedux(aplSettings.siteId) as any);
        return () => {
            dispatch(onClearAdminRedux());
        };
    }, [aplSettings.siteId, dispatch, isGlobalAdmin]);

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-8">
                    <DtGrid2 options={linePrintersGridOptions}>
                        <div
                            id="div-bottom-buttons"
                            className="row float-right btn-group margin-right-0 buttoms-shift-up"
                            role="group"
                            aria-label="Bottom action buttons"
                        >
                            <button
                                className="btn btn-primary"
                                onClick={savePrinterLineSelection}
                                disabled={!adminRedux.printerLineSelectionDirty}
                            >
                                Save
                            </button>
                        </div>
                    </DtGrid2>
                </div>
                <div className="col-4">
                    <DtGrid2 options={departmentPrintersGridOptions}>
                        <div
                            id="div-bottom-buttons"
                            className="row float-right btn-group margin-right-0 buttoms-shift-up"
                            role="group"
                            aria-label="Bottom action buttons"
                        >
                            <button className="btn btn-primary" onClick={savePrinterDepartment} disabled={!adminRedux.departmentsDirty}>
                                Save
                            </button>
                        </div>
                    </DtGrid2>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AdminPrinterLine;